/**
 * Saved Views API Types
 *
 * TypeScript interfaces for the saved views & dynamic lists feature.
 * Aligned with the backend saved-views module OpenAPI specification.
 * @see http://localhost:8080/docs-json
 */

// ─── Entity Types ───────────────────────────────────────────────────

/** Entity types that support saved views */
export type SavedViewEntityType = 'property' | 'tenant' | 'contact' | 'lead' | 'weather_event' | 'project' | 'weather_report' | 'weather_report_lead'
    | 'fleet_machine' | 'fleet_app_classification' | 'fleet_domain_classification' | 'fleet_daily_summary'
    | 'contract_review' | 'optimizer_job' | 'optimizer_material_preset' | 'it_request' | 'fab_job'

/** View type — dynamic re-queries every load, static is a frozen snapshot */
export type SavedViewType = 'dynamic' | 'static'

/** Visibility scope */
export type SavedViewVisibility = 'private' | 'shared' | 'system'

// ─── Filter Types ───────────────────────────────────────────────────

/** Supported filter operators */
export type FilterOperator =
    | '$eq'
    | '$ne'
    | '$gt'
    | '$gte'
    | '$lt'
    | '$lte'
    | '$in'
    | '$nin'
    | '$contains'
    | '$regex'
    | '$startsWith'
    | '$endsWith'
    | '$exists'

/** Single filter condition — leaf node in the filter tree */
export interface FilterCondition {
    field: string
    op: FilterOperator
    value: unknown
}

/**
 * Recursive filter group — supports AND/OR nesting.
 * Each group contains conditions and/or nested groups.
 */
export interface FilterGroup {
    operator: 'AND' | 'OR'
    conditions: (FilterCondition | FilterGroup)[]
}

/** Type guard to distinguish FilterCondition from FilterGroup */
export function isFilterCondition(node: FilterCondition | FilterGroup): node is FilterCondition {
    return 'field' in node && 'op' in node
}

/** Type guard for FilterGroup */
export function isFilterGroup(node: FilterCondition | FilterGroup): node is FilterGroup {
    return 'operator' in node && 'conditions' in node
}

// ─── Sort ───────────────────────────────────────────────────────────

/** Sort configuration */
export interface SortConfig {
    field: string
    direction: 'asc' | 'desc'
}

// ─── Saved View Entity ──────────────────────────────────────────────

/** Full saved view entity returned by the API */
export interface SavedView {
    id: string
    name: string
    entityType: SavedViewEntityType
    type: SavedViewType
    visibility: SavedViewVisibility
    filters?: FilterGroup
    sort?: SortConfig
    /** IDs of frozen entities (only for static views) */
    staticEntityIds?: string[]
    /** Timestamp when the view was frozen (only for static views) */
    frozenAt?: string
    /** Whether this view is marked as the default landing view */
    isDefault?: boolean
    /** Display order within the entity type */
    position: number
    createdBy: { id: string } | null
    createdAt: string
    updatedAt: string
}

// ─── DTOs ───────────────────────────────────────────────────────────

/** Create a new saved view */
export interface CreateSavedViewDto {
    name: string
    entityType: SavedViewEntityType
    filters?: FilterGroup
    sort?: SortConfig
    visibility?: SavedViewVisibility
}

/** Update an existing saved view (all fields optional) */
export interface UpdateSavedViewDto {
    name?: string
    filters?: FilterGroup
    sort?: SortConfig
    visibility?: SavedViewVisibility
    position?: number
}

// ─── Schema Types ───────────────────────────────────────────────────

/** Field type for schema-driven filter rendering */
export type SchemaFieldType = 'string' | 'number' | 'boolean' | 'virtual-boolean' | 'enum' | 'date' | 'relation'

/** Single field in an entity schema */
export interface SchemaField {
    name: string
    type: SchemaFieldType
    label: string
    /** Allowed operators for this field */
    operators: FilterOperator[]
    /** Enum values (only for enum-type fields) */
    values?: string[]
    /** For relation fields — the related entity name returned by the schema (e.g., "id") */
    relationField?: string
}

/** Schema for an entity type — describes available filterable fields */
export interface EntitySchema {
    entityType: SavedViewEntityType
    label: string
    fields: SchemaField[]
    systemViews?: Array<{
        name: string
        entityType?: SavedViewEntityType
        filters?: FilterGroup
        sort?: SortConfig[] | SortConfig
        position?: number
    }>
}

// ─── Request / Response Types ───────────────────────────────────────

/** Query params for listing saved views */
export interface SavedViewListParams {
    entityType?: SavedViewEntityType
    visibility?: SavedViewVisibility
}

/** Request body for previewing filter results */
export interface PreviewRequest {
    entityType: SavedViewEntityType
    filters: FilterGroup
    sort?: SortConfig
    page?: number
    limit?: number
}

/** Response for count endpoints */
export interface CountResponse {
    count: number
}

/** Response wrapper for single-item API responses */
export interface ApiResponse<T> {
    ok: boolean
    data: T
}

/** Item shape returned for relation fields from the values endpoint */
export interface FieldValueRelationItem {
    id: string
    label: string
}

/** Response from GET /saved-views/schema/:entityType/values/:field */
export type FieldValuesResponse = string[] | FieldValueRelationItem[]

// ─── Dynamic Tokens ───────────────────────────────────────────────

/** Dynamic variable tokens supported in filter values */
export type DynamicToken =
    | '$CURRENT_USER'
    | '$TODAY'
    | '$YESTERDAY'
    | '$WEEK'
    | '$LAST_WEEK'
    | '$MONTH'
    | '$LAST_MONTH'
    | '$NOW'

/** Token metadata for UI selection */
export interface DynamicTokenOption {
    token: DynamicToken
    label: string
    description: string
    applicableTypes: ('string' | 'relation' | 'date')[]
}

/** Registry of dynamic tokens with human-friendly labels and target field types */
export const DYNAMIC_TOKENS: DynamicTokenOption[] = [
    { token: '$CURRENT_USER', label: 'Current User', description: "Authenticated user's account", applicableTypes: ['relation'] },
    { token: '$TODAY', label: 'Today', description: 'Start of today (00:00:00.000Z)', applicableTypes: ['date'] },
    { token: '$YESTERDAY', label: 'Yesterday', description: 'Start of yesterday (00:00:00.000Z)', applicableTypes: ['date'] },
    { token: '$WEEK', label: 'This Week', description: 'Start of current week (Sunday 00:00:00.000Z)', applicableTypes: ['date'] },
    { token: '$LAST_WEEK', label: 'Last Week', description: 'Start of previous week (Sunday 00:00:00.000Z)', applicableTypes: ['date'] },
    { token: '$MONTH', label: 'This Month', description: 'Start of current month (1st 00:00:00.000Z)', applicableTypes: ['date'] },
    { token: '$LAST_MONTH', label: 'Last Month', description: 'Start of previous month (1st 00:00:00.000Z)', applicableTypes: ['date'] },
    { token: '$NOW', label: 'Now', description: 'Current UTC timestamp', applicableTypes: ['date'] },
]

/** Format a token string (e.g. "$TODAY") into a human-readable label ("Today") */
export function formatTokenValue(value: unknown): string {
    if (typeof value !== 'string') return String(value ?? '')
    const found = DYNAMIC_TOKENS.find(t => t.token === value)
    return found ? found.label : value
}

